import { IInboundEmailCommunication } from 'shared';

import { Drawer } from '../../../../common/Atoms/Drawer';
import { Heading } from '../../../../common/Atoms/Typography/Heading';
import { Paragraph } from '../../../../common/Atoms/Typography/Paragraph';
import { formatInboxDate } from '../common/formatInboxDate';

interface EmailInboxMessageDrawerProps {
  email: IInboundEmailCommunication | null;
  open: boolean;
  onClose: () => void;
}

function sanitizeLinks(content?: string) {
  if (!content) {
    return content;
  }

  return content
    .replace(/<a\b[^>]*>[\s\S]*?<\/a>/gi, `[LINK]`)
    .replace(/https?:\/\/[^\s<>"')\]]+/gi, `[LINK]`)
    .replace(/www\.[^\s<>"')\]]+/gi, `[LINK]`);
}

function stripHtmlTags(content?: string) {
  if (!content) {
    return ``;
  }

  return content
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, ``)
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, ``)
    .replace(/<br\s*\/?>/gi, `\n`)
    .replace(/<\/(p|div|li|tr|h[1-6])>/gi, `\n`)
    .replace(/<[^>]+>/g, ``)
    .replace(/\n{3,}/g, `\n\n`)
    .trim();
}

export function EmailInboxMessageDrawer({
  email,
  open,
  onClose,
}: EmailInboxMessageDrawerProps) {
  const sanitizedReplyText = sanitizeLinks(email?.extractedReplyText);
  const sanitizedTextBody = sanitizeLinks(email?.textBody);
  const sanitizedHtmlBody = sanitizeLinks(email?.htmlBody);
  const emailHistoryText = sanitizedTextBody || stripHtmlTags(sanitizedHtmlBody);

  return (
    <Drawer
      title={ email?.subject || `Inbound Email` }
      open={ open }
      setOpen={ isOpen => {
        if (!isOpen) {
          onClose();
        }
      } }
      size={ `xl` }
    >
      { email ? (
        <div className={ `space-y-5` }>
          <div className={ `rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700` }>
            <div className={ `mb-1` }>
              <span className={ `font-semibold` }>{ `Received: ` }</span>
              { formatInboxDate(email.createdAt) }
            </div>
            <div className={ `mb-1` }>
              <span className={ `font-semibold` }>{ `From: ` }</span>
              { email.forwardedFrom || email.from || `-` }
            </div>
            { email.forwardedFrom ? (
              <div className={ `mb-1` }>
                <span className={ `font-semibold` }>{ `Forwarded by: ` }</span>
                { email.from }
              </div>
            ) : null }
            <div>
              <span className={ `font-semibold` }>{ `To: ` }</span>
              { email.to || `-` }
            </div>
          </div>

          <div>
            <Heading size={ `md` }>{ `Reply text` }</Heading>
            <div className={ `mt-2 rounded-lg border border-emerald-100 bg-emerald-50 p-3` }>
              <Paragraph className={ `whitespace-pre-wrap wrap-break-word` }>
                { sanitizedReplyText || `No extracted reply text available.` }
              </Paragraph>
            </div>
          </div>

          <div>
            <Heading size={ `md` }>{ `Email History` }</Heading>
            { emailHistoryText ? (
              <div className={ `mt-2 rounded-lg border border-gray-200 bg-white p-4 shadow-xs` }>
                <Paragraph className={ `whitespace-pre-wrap wrap-break-word text-gray-800` }>
                  { emailHistoryText }
                </Paragraph>
              </div>
            ) : (
              <div className={ `mt-2 rounded-lg border border-gray-200 bg-gray-50 p-4` }>
                <Paragraph>
                  { `No message body available.` }
                </Paragraph>
              </div>
            ) }
          </div>
        </div>
      ) : null }
    </Drawer>
  );
}
